import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, RuleElementListHeader, useAutoScrolling, useDeepCompare, useElementToggle, useOnMount } from '@wix/rules-panel-ui';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';
import { currentElementInfoAtom } from '../../../atoms/bi-atoms';
import { scrollerNodeRefAtom } from '../../../atoms/session-atoms';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
import { convertBooleanStatusToElementStatus } from '../alt-image-fixer/utils';
import { useInaccessibleComponentsAnalytics } from './hooks/use-inaccessible-components-analytics';
import { useSyncSessionComponents } from './hooks/use-sync-session-components';
import { InaccessibleComponentsRule } from './inaccessible-components-rule';
export const InaccessibleComponentsRuleFixer = _ref => {
  let {
    failedElements,
    pageId,
    elements
  } = _ref;
  const {
    t
  } = useTranslation();
  const setCurrentElementInfo = useSetAtom(currentElementInfoAtom);
  const {
    openNextElement,
    toggleElement,
    selectedElementId
  } = useElementToggle();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const scrollerNodeRef = useAtomValue(scrollerNodeRefAtom);
  const {
    selectedItemContentRef,
    selectedItemHeaderRef
  } = useAutoScrolling(selectedElementId, scrollerNodeRef);
  useSyncSessionComponents(failedElements, pageId);
  const {
    manualTaskDataEvent
  } = useInaccessibleComponentsAnalytics({
    elements,
    failedElements
  });
  useOnMount(manualTaskDataEvent);
  const stableElements = useDeepCompare(elements);
  const memoizedElements = useMemo(() => (stableElements == null ? void 0 : stableElements.map(el => ({
    breakpointId: el.breakpointId,
    id: el.id,
    status: convertBooleanStatusToElementStatus(el.isFixed ?? false)
  }))) ?? [], [stableElements]);
  const openInaccessibleComponentsElement = useCallback(_ref2 => {
    let {
      id: elementId,
      breakpointId
    } = _ref2;
    const selectedElementIndex = memoizedElements.findIndex(el => el.id === elementId);
    setCurrentElementInfo({
      elementIndex: selectedElementIndex,
      elementStatus: convertElementStatusToBooleanStatus(memoizedElements[selectedElementIndex].status)
    });
    const nextCompRef = {
      id: elementId,
      type: getCompRefType(editorHost, breakpointId)
    };
    a11yWizardAdapter.selectComponent(nextCompRef).catch(() => {});
  }, [a11yWizardAdapter, memoizedElements, setCurrentElementInfo, editorHost]);
  useEffect(() => {
    openNextElement(memoizedElements, openInaccessibleComponentsElement, () => {});
  }, [memoizedElements, openInaccessibleComponentsElement, openNextElement]);
  const CompletedHeader = () => /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin",
    secondary: true
  }, t('accessibility-wizard.rules-list.inaccessible-components.list.completed.header'));
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2"
  }, elements.length === 0 ? /*#__PURE__*/React.createElement(CompletedHeader, null) : /*#__PURE__*/React.createElement(RuleElementListHeader, {
    completedSize: elements.filter(el => el.isFixed).length,
    title: t('accessibility-wizard.rules-list.inaccessible-components.list.failed-elements-number.heading'),
    totalSize: elements.length
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2"
  }, elements.map((element, index) => {
    const {
      id,
      breakpointId
    } = element;
    const onOpenElement = () => {
      const compRef = {
        id,
        type: getCompRefType(editorHost, breakpointId)
      };
      a11yWizardAdapter.selectComponent(compRef).catch(() => {});
    };
    return /*#__PURE__*/React.createElement(InaccessibleComponentsRule, {
      contentRef: selectedItemContentRef,
      element: element,
      elementIndex: index,
      headerRef: selectedItemHeaderRef,
      isOpen: selectedElementId === id,
      key: id,
      pageId: pageId,
      toggleElement: toggleElement(id, onOpenElement)
    });
  })));
};
//# sourceMappingURL=inaccessible-components-rule-fixer.js.map